import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { checkAdminExists, adminLogin, adminSetup } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

export default function AdminLogin() {
  const navigate = useNavigate();
  const { isAuthenticated, login } = useAuth();
  const [isSetup, setIsSetup] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
  });

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/admin/dashboard');
      return;
    }
    
    const checkSetup = async () => {
      try {
        const response = await checkAdminExists();
        setIsSetup(!response.data.exists);
      } catch (error) {
        console.error('Error checking admin:', error);
      } finally {
        setLoading(false);
      }
    };
    checkSetup();
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.username || !formData.password) {
      toast.error('Wypełnij wszystkie pola');
      return;
    }

    if (isSetup && formData.password !== formData.confirmPassword) {
      toast.error('Hasła nie są identyczne');
      return;
    }

    if (isSetup && formData.password.length < 6) {
      toast.error('Hasło musi mieć minimum 6 znaków');
      return;
    }

    setSubmitting(true);
    try {
      const response = isSetup
        ? await adminSetup({ username: formData.username, password: formData.password })
        : await adminLogin({ username: formData.username, password: formData.password });

      login(response.data.token, response.data.username);
      toast.success(isSetup ? 'Konto utworzone!' : 'Zalogowano pomyślnie!');
      navigate('/admin/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Błąd logowania');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0F1C]">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0F1C] px-4" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="bg-[#111827] rounded-2xl shadow-xl border border-cyan-500/20 p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-cyan-400 to-cyan-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-cyan-500/30">
              <Camera className="w-8 h-8 text-[#0A0F1C]" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">
              {isSetup ? 'Utwórz konto administratora' : 'Panel Administratora'}
            </h1>
            <p className="text-slate-400">
              {isSetup ? 'Ustaw dane logowania do panelu' : 'Zaloguj się, aby zarządzać stroną'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="admin-login-form">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Nazwa użytkownika
              </label>
              <Input
                type="text"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                className="h-12 rounded-lg bg-[#0A0F1C] border-cyan-500/20 text-white placeholder:text-slate-500 focus:border-cyan-400"
                placeholder="admin"
                data-testid="admin-username-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Hasło
              </label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="h-12 rounded-lg pr-12 bg-[#0A0F1C] border-cyan-500/20 text-white placeholder:text-slate-500 focus:border-cyan-400"
                  placeholder="••••••••"
                  data-testid="admin-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-cyan-400"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {isSetup && (
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Potwierdź hasło
                </label>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  className="h-12 rounded-lg bg-[#0A0F1C] border-cyan-500/20 text-white placeholder:text-slate-500 focus:border-cyan-400"
                  placeholder="••••••••"
                  data-testid="admin-confirm-password-input"
                />
              </div>
            )}

            <Button
              type="submit"
              disabled={submitting}
              className="w-full h-12 rounded-full bg-gradient-to-r from-cyan-400 to-cyan-600 text-[#0A0F1C] hover:from-cyan-300 hover:to-cyan-500 font-semibold shadow-lg shadow-cyan-500/30"
              data-testid="admin-submit-button"
            >
              {submitting ? 'Proszę czekać...' : isSetup ? 'Utwórz konto' : 'Zaloguj się'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <a href="/" className="text-sm text-slate-400 hover:text-cyan-400 transition-colors">
              ← Powrót do strony głównej
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
